import { STATISTIC_TYPE, DTAB_STATISTIC_TYPES } from "types/ReportingDashboard/elements.types"
import { TOTALS_KEY } from "constants/tableConversion"
import { RawDataCell, RawDataRow } from "types/tableConversion.types"

/**
 * Sorts keys numerically, placing "Totals" at the end
 */
export const sortKeysNumerically = (keys: string[]): string[] => {
  return [...keys].sort((a: string, b: string): number => {
    if (a === TOTALS_KEY) return 1
    if (b === TOTALS_KEY) return -1

    const numA = parseFloat(a)
    const numB = parseFloat(b)

    if (!isNaN(numA) && !isNaN(numB)) {
      return numA - numB
    }

    if (!isNaN(numA)) return -1
    if (!isNaN(numB)) return 1

    return a.localeCompare(b)
  })
}

/**
 * Checks if a value is a valid STATISTIC_TYPE enum member
 */
export const isStatisticType = (value: unknown): value is STATISTIC_TYPE => {
  return typeof value === "string" && Object.values(STATISTIC_TYPE).includes(value as STATISTIC_TYPE)
}

/**
 * Checks if a statistic type is used in DTAB format
 */
export const isDtabStatisticType = (statisticType: STATISTIC_TYPE): boolean => {
  return (DTAB_STATISTIC_TYPES as readonly STATISTIC_TYPE[]).includes(statisticType)
}

/**
 * Checks if a statistic value indicates percentage formatting
 */
export const isPercentageStatistic = (statisticValue: RawDataCell): boolean => {
  if (typeof statisticValue !== "string") return false
  
  const percentageTypes = [
    STATISTIC_TYPE.ROW_PERCENT,
    STATISTIC_TYPE.COL_PERCENT,
    STATISTIC_TYPE.PERC_PERCENT,
  ]
  
  return percentageTypes.includes(statisticValue as STATISTIC_TYPE) ||
    statisticValue.toLowerCase().includes("perc") ||
    statisticValue.toLowerCase().includes("percent") ||
    statisticValue.toLowerCase().includes("%")
}

/**
 * Converts a numeric value to percentage format
 */
export const convertToPercentage = (value: RawDataCell): string | number | null => {
  if (typeof value !== "string" && typeof value !== "number") return value
  
  const num = parseFloat(String(value))
  if (isNaN(num)) return value
  
  return num > 1 && num <= 100
    ? `${num.toFixed(2)}%`
    : `${(num * 100).toFixed(2)}%`
}

/**
 * Checks if data has a "Totals" row
 */
export const hasTotalsRow = (dataRows: RawDataRow[]): boolean => {
  return dataRows.some(row => row[0] === TOTALS_KEY)
}

/**
 * Gets unique statistic types from data rows
 */
export const getUniqueStatisticTypes = (dataRows: RawDataRow[]): string[] => {
  const statisticTypes = dataRows
    .filter(row => row[0] !== TOTALS_KEY)
    .map(row => row[1])
    .filter(Boolean)
    .map(stat => String(stat))

  return [...new Set(statisticTypes)]
}

/**
 * Creates empty transformed data structure
 */
export const createEmptyTransformedData = () => ({
  headers: [],
  rows: [],
  hasSubHeaders: false,
  barChartData: {
    keys: [],
    data: [],
    xAxisLabel: "",
    statisticTypes: [],
  },
  pieChartData: {
    categoryData: [],
    subCategoryData: [],
    statisticTypes: [],
  },
})